/**
 * Punto único de carga de pdf.js.
 *
 * COMPATIBILIDAD DE NAVEGADOR (causa de "documentos que se ven a rayas / en blanco"):
 * pdf.js 6.x usa `Map.prototype.getOrInsertComputed` (propuesta ES muy reciente).
 * En navegadores que todavía no la implementan, `page.render()` y
 * `page.getOperatorList()` lanzan "getOrInsertComputed is not a function": el PDF
 * no se pinta (se ve en blanco o a franjas) y la imagen que se envía a la IA es
 * inservible, por lo que el documento se marca como no procesado.
 *
 * Solución: instalamos el polyfill en el hilo principal y, cuando falta de forma
 * nativa, también dentro del worker (se sirve una copia del worker con el
 * polyfill inyectado a través de un blob URL).
 */
import * as pdfjsLib from "pdfjs-dist";

// Use Vite's ?url import to get a stable asset URL that works in both dev and production
import workerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";

const POLYFILL_SRC = `(() => {
  const define = (proto, name, fn) => {
    if (!proto || typeof proto[name] === "function") return;
    Object.defineProperty(proto, name, { value: fn, writable: true, configurable: true, enumerable: false });
  };
  function getOrInsertComputed(key, callback) {
    if (this.has(key)) return this.get(key);
    const value = callback(key);
    this.set(key, value);
    return value;
  }
  function getOrInsert(key, value) {
    if (this.has(key)) return this.get(key);
    this.set(key, value);
    return value;
  }
  for (const ctor of [Map, WeakMap]) {
    define(ctor.prototype, "getOrInsertComputed", getOrInsertComputed);
    define(ctor.prototype, "getOrInsert", getOrInsert);
  }
})();`;

const needsPolyfill =
  typeof (Map.prototype as unknown as Record<string, unknown>).getOrInsertComputed !== "function";

// 1) Hilo principal
if (needsPolyfill) {
  // eslint-disable-next-line no-eval
  (0, eval)(POLYFILL_SRC);
}

pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;

/**
 * Se resuelve cuando pdf.js está listo para abrir documentos. En navegadores
 * modernos resuelve de inmediato; en los antiguos espera a tener preparado el
 * worker parcheado. Espera a esta promesa antes de llamar a `getDocument`.
 */
export const pdfjsReady: Promise<void> = (async () => {
  if (!needsPolyfill) return;
  try {
    const res = await fetch(workerUrl);
    if (!res.ok) return;
    const src = await res.text();
    const blob = new Blob([POLYFILL_SRC, "\n", src], { type: "text/javascript" });
    pdfjsLib.GlobalWorkerOptions.workerSrc = URL.createObjectURL(blob);
  } catch (e) {
    console.warn("No se pudo preparar el worker de pdf.js con polyfill:", e);
  }
})();

/**
 * Recursos que pdf.js 6 necesita para decodificar imágenes escaneadas (JBIG2 /
 * JPEG2000 vía WASM), fuentes estándar y CMaps. Sin `wasmUrl` los documentos
 * escaneados con JBIG2 se ven vacíos o "a rayas" y la IA no puede leerlos.
 * Los archivos se sirven desde /public/pdfjs.
 */
const assetBase = `${import.meta.env.BASE_URL || "/"}pdfjs/`.replace(/\/{2,}/g, "/");

export const PDF_ASSET_PARAMS = {
  wasmUrl: `${assetBase}wasm/`,
  iccUrl: `${assetBase}wasm/`,
  standardFontDataUrl: `${assetBase}standard_fonts/`,
  cMapUrl: `${assetBase}cmaps/`,
  cMapPacked: true,
} as const;

/** Abre un PDF con todos los recursos y polyfills ya preparados. */
export async function getPdfDocument(
  params: Record<string, unknown>,
): Promise<ReturnType<typeof pdfjsLib.getDocument>> {
  await pdfjsReady;
  return pdfjsLib.getDocument({ ...PDF_ASSET_PARAMS, ...params } as never);
}

export { pdfjsLib };

